import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * One requestAnimationFrame loop for a scene. `onFrame` mutates SVG attributes
 * through refs — never React state — so 60 fps costs no re-renders. The loop
 * pauses while the tab is hidden or the target is scrolled off-screen, and
 * never starts when `enabled` is false (reduced motion).
 */
export function useSceneClock(
  targetRef: RefObject<Element | null>,
  onFrame: (t: number) => void,
  enabled: boolean,
): void {
  const frameRef = useRef(onFrame);
  useLayoutEffect(() => {
    frameRef.current = onFrame;
  });

  useEffect(() => {
    if (!enabled || typeof window.requestAnimationFrame !== 'function') return;
    let raf = 0;
    let onScreen = true;
    const tick = (t: number) => {
      frameRef.current(t);
      raf = window.requestAnimationFrame(tick);
    };
    const start = () => {
      if (!raf && onScreen && document.visibilityState !== 'hidden') raf = window.requestAnimationFrame(tick);
    };
    const stop = () => {
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
    };
    const onVisibility = () => (document.visibilityState === 'hidden' ? stop() : start());
    document.addEventListener('visibilitychange', onVisibility);
    const target = targetRef.current;
    const observer =
      target && typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(([entry]) => {
            onScreen = entry?.isIntersecting ?? true;
            if (onScreen) start();
            else stop();
          })
        : null;
    if (target && observer) observer.observe(target);
    start();
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      observer?.disconnect();
    };
  }, [enabled, targetRef]);
}
